import type { Column, Row } from '@tanstack/react-table';
import type { ExportableColumnDef } from '~/components2/Table';

export interface GetColumnDefsFromTableProps<T> {
  columns: Column<T, unknown>[];
  exclude?: string[];
}

export interface ExcelColumnDef {
  id: string;
  header: string;
  columns?: ExcelColumnDef[];
}

export interface ExcelSheetDef {
  name: string;
  columnDefs: ExcelColumnDef[];
  rows: any[][];
}

function getColumnDefsForHeader<T>(columns: Column<T>[]): ExcelColumnDef[] {
  return columns.map((c) => ({
    id: c.id!,
    header: getColumnHeaderContent(c),
    ...(c.columns?.length && { columns: getColumnDefsForHeader(c.columns) }),
  }));
}

export function createExcelSheetDef<T>({
  name,
  columns,
  rows,
}: {
  name: string;
  rows: Row<T>[];
  columns: Column<T>[];
}) {
  const columnDefs = getColumnDefsForHeader(columns);

  const rowData = rows.map((row) => row.getVisibleCells().map((cell) => cell.getValue()));

  const sheetData: ExcelSheetDef = {
    name,
    columnDefs,
    rows: rowData,
  };

  return sheetData;
}

export function downloadExcelFileBuffer(buffer: Buffer, fileName: string) {
  // @ts-expect-error <-- @types/bun and @types/node conflict here
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${fileName}.xlsx`;
  a.click();
  window.URL.revokeObjectURL(url);
}

export function getColumnHeaderContent<T>(column: Column<T, unknown>): string {
  const def = column.columnDef as ExportableColumnDef<Row<T>>;
  if (def.exportLabel) {
    return def.exportLabel;
  } else if (def.header) {
    return def.header.toString();
  } else if (column.id) {
    return column.id;
  } else {
    return '';
  }
}

export const WHITESPACE_REGEX_PATTERN = /[\r\n\t\f\v]/g;

export function removeWhitespaceRegEx(original: string) {
  const text = original.replace(WHITESPACE_REGEX_PATTERN, '');
  return text;
}

export function formatCellValueForCSV(value: unknown) {
  return removeWhitespaceRegEx((value ?? '')?.toString()).replaceAll(',', '');
}

function isLastItem(i: number, arr: any[]) {
  return i === arr.length - 1;
}

export function createCsvDataFromTable<T>({ rows, columns }: { rows: Row<T>[]; columns: Column<T>[] }) {
  let csvData: string = '';

  // CSVs can only have one header row
  columns.forEach((c, i) => {
    const header = formatCellValueForCSV(getColumnHeaderContent(c));
    csvData += `${header}${isLastItem(i, columns) ? '' : ','}`;
  });

  rows.forEach((row) => {
    let rowData = '';
    const cells = row.getVisibleCells();
    cells.forEach((cell, i) => {
      const value = formatCellValueForCSV(cell.getValue());
      rowData += `${value}${isLastItem(i, cells) ? '' : ','}`;
    });

    if (rowData) {
      csvData += `\n${rowData}`;
    }
  });

  return csvData;
}
